import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { analyticsService } from '../../../services/request/analytics';
import { card_services } from '../../../services/request/card';
import { engagementService } from '../../../services/request/engagement';
import { STATUS } from '../../../services/request/card.interface';
import { useT } from '../../../i18n/LanguageProvider';

const TIME_SLICES = ['Last 7 Days', 'Last 15 Days', 'Last 30 Days', 'Last 2 months', 'Last 6 months', 'Last year', 'All cards'];

// Map internal slice keys (used for date logic) to i18n keys for display.
const SLICE_KEYS: Record<string, string> = {
	'Last 7 Days': 'last7Days',
	'Last 15 Days': 'last15Days',
	'Last 30 Days': 'last30Days',
	'Last 2 months': 'last2Months',
	'Last 6 months': 'last6Months',
	'Last year': 'lastYear',
	'All cards': 'allCards',
};

const getDateFromSlice = (slice: string) => {
	const date = new Date();
	if (slice === 'Last 30 Days') date.setMonth(date.getMonth() - 1);
	else if (slice === 'Last 15 Days') date.setDate(date.getDate() - 15);
	else if (slice === 'Last 7 Days') date.setDate(date.getDate() - 7);
	else if (slice === 'Last year') date.setFullYear(date.getFullYear() - 1);
	else if (slice === 'Last 2 months') date.setMonth(date.getMonth() - 2);
	else if (slice === 'Last 6 months') date.setMonth(date.getMonth() - 6);
	else if (slice === 'All cards') date.setFullYear(date.getFullYear() - 3);
	return date.getTime();
};

const EngagementPage: React.FC = () => {
	const { t } = useT();
	const currentWorkspace = useSelector((state: any) => state.currentWorkspace);
	const [activeTimeSlice, setActiveTimeSlice] = useState('Last 30 Days');
	const [openTimeSlice, setOpenTimeSlice] = useState(false);
	const [overviewLoading, setOverviewLoading] = useState(false);
	const [cardsLoading, setCardsLoading] = useState(false);

	// Overview stats
	const [overview, setOverview] = useState({
		emailOpens: 0,
		cardOpens: 0,
		totalReactions: 0,
		totalComments: 0,
		totalDownloads: 0,
		contactEngagement: 0,
	});

	// Per-card data
	const [cards, setCards] = useState<any[]>([]);
	const [expandedCard, setExpandedCard] = useState<string | null>(null);
	const [cardEngagement, setCardEngagement] = useState<{ [cardId: string]: { reactions: any; comments: any[]; cardOpens: number; emailOpens: number; loading: boolean } }>({});

	useEffect(() => {
		if (currentWorkspace?._id) {
			fetchOverview();
			fetchCards();
		}
	}, [activeTimeSlice, currentWorkspace?._id]);

	const fetchOverview = async () => {
		setOverviewLoading(true);
		try {
			const isodate = getDateFromSlice(activeTimeSlice);
			const [campaign, contact] = await Promise.all([
				analyticsService.campaignAnalytics(currentWorkspace._id, isodate),
				analyticsService.contactEngagement(currentWorkspace._id, isodate),
			]);
			if (campaign?.length) {
				setOverview({
					emailOpens: campaign[0].emailOpens || 0,
					cardOpens: campaign[0].cardOpens || 0,
					totalReactions: campaign[0].totalReactions || 0,
					totalComments: campaign[0].totalComments || 0,
					totalDownloads: campaign[0].totalDownloads || 0,
					contactEngagement: contact?.[0]?.recipientInfo || 0,
				});
			} else {
				setOverview({ emailOpens: 0, cardOpens: 0, totalReactions: 0, totalComments: 0, totalDownloads: 0, contactEngagement: 0 });
			}
		} catch (err) {
			console.error('Error fetching overview:', err);
		} finally {
			setOverviewLoading(false);
		}
	};

	const fetchCards = async () => {
		setCardsLoading(true);
		try {
			const date: any = new Date();
			date.setFullYear(date.getFullYear() - 3);
			const res = await card_services.getCardsByWorkspace(currentWorkspace._id, date);
			const sentCards = (res || []).filter((c: any) => c.status === STATUS.SENT || c.status === 'sent' || c.status === STATUS.WORKFLOW);
			setCards(sentCards);
		} catch (err) {
			console.error('Error fetching cards:', err);
		} finally {
			setCardsLoading(false);
		}
	};

	const handleExpandCard = async (cardId: string) => {
		if (expandedCard === cardId) {
			setExpandedCard(null);
			return;
		}
		setExpandedCard(cardId);

		if (cardEngagement[cardId]) return;

		setCardEngagement((prev) => ({ ...prev, [cardId]: { reactions: {}, comments: [], cardOpens: 0, emailOpens: 0, loading: true } }));

		try {
			const [reactions, comments, analytics] = await Promise.all([
				engagementService.getReactions(cardId).catch(() => ({})),
				engagementService.getComments(cardId).catch(() => []),
				analyticsService.getSingleCardAnalytics(cardId).catch(() => null),
			]);
			setCardEngagement((prev) => ({
				...prev,
				[cardId]: {
					reactions: reactions?.grouped || reactions || {},
					comments: comments || [],
					cardOpens: analytics?.cardOpens?.length || 0,
					emailOpens: analytics?.emailOpens?.length || 0,
					loading: false,
				},
			}));
		} catch {
			setCardEngagement((prev) => ({ ...prev, [cardId]: { ...prev[cardId], loading: false } }));
		}
	};

	const statCards = [
		{ label: t('engagement.statContactEngagement'), value: overview.contactEngagement, icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z' },
		{ label: t('engagement.statEmailsOpened'), value: overview.emailOpens, icon: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' },
		{ label: t('engagement.statCardsOpened'), value: overview.cardOpens, icon: 'M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z' },
		{ label: t('engagement.statTotalReactions'), value: overview.totalReactions, icon: 'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z' },
		{ label: t('engagement.statTotalComments'), value: overview.totalComments, icon: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z' },
		{ label: t('engagement.statTotalDownloads'), value: overview.totalDownloads, icon: 'M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4' },
	];

	return (
		<div className="p-6 max-w-7xl mx-auto">
			{/* Header */}
			<div className="flex items-center justify-between mb-6">
				<h1 className="text-2xl font-bold text-gray-900">{t('engagement.title')}</h1>
				<div className="relative">
					<button
						onClick={() => setOpenTimeSlice(!openTimeSlice)}
						className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
					>
						{t('engagement.' + (SLICE_KEYS[activeTimeSlice] || 'last30Days'))}
						<svg className="w-3 h-3" viewBox="0 0 11 6" fill="none">
							<path fillRule="evenodd" clipRule="evenodd" d="M0.295 0.293C0.685-0.098 1.319-0.098 1.709 0.293L5.002 3.586 8.295 0.293C8.685-0.098 9.319-0.098 9.709 0.293 10.1 0.683 10.1 1.317 9.709 1.707L5.709 5.707C5.319 6.098 4.685 6.098 4.295 5.707L0.295 1.707C-0.096 1.317-0.096 0.683 0.295 0.293Z" fill="#6B7280" />
						</svg>
					</button>
					{openTimeSlice && (
						<div className="absolute right-0 mt-1 z-50 bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[160px]">
							{TIME_SLICES.map((slice) => (
								<button
									key={slice}
									onClick={() => { setActiveTimeSlice(slice); setOpenTimeSlice(false); }}
									className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${activeTimeSlice === slice ? 'text-indigo-600 font-medium bg-indigo-50' : 'text-gray-700'}`}
								>
									{t('engagement.' + (SLICE_KEYS[slice] || 'last30Days'))}
								</button>
							))}
						</div>
					)}
				</div>
			</div>

			{/* Overview Stats Grid */}
			<div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
				{statCards.map((stat) => (
					<div key={stat.label} className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
						<div className="flex items-center gap-3 mb-2">
							<div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
								<svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
									<path strokeLinecap="round" strokeLinejoin="round" d={stat.icon} />
								</svg>
							</div>
						</div>
						{overviewLoading ? (
							<div className="h-8 bg-gray-100 rounded animate-pulse" />
						) : (
							<p className="text-2xl font-bold text-gray-900">{stat.value}</p>
						)}
						<p className="text-xs text-gray-500 mt-1">{stat.label}</p>
					</div>
				))}
			</div>

			{/* Per-Card Engagement */}
			<div>
				<h2 className="text-lg font-semibold text-gray-900 mb-4">{t('engagement.cardEngagement')}</h2>
				{cardsLoading ? (
					<div className="space-y-3">
						{[1, 2, 3].map((i) => (
							<div key={i} className="bg-white rounded-xl border border-gray-200 p-4 animate-pulse">
								<div className="flex items-center gap-4">
									<div className="w-16 h-12 bg-gray-200 rounded" />
									<div className="flex-1">
										<div className="h-4 bg-gray-200 rounded w-1/3 mb-2" />
										<div className="h-3 bg-gray-100 rounded w-1/4" />
									</div>
								</div>
							</div>
						))}
					</div>
				) : cards.length === 0 ? (
					<div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
						<p className="text-gray-500">{t('engagement.noSentCards')}</p>
					</div>
				) : (
					<div className="space-y-3">
						{cards.map((card: any) => (
							<div key={card._id} className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
								<button
									onClick={() => handleExpandCard(card._id)}
									className="w-full flex items-center gap-4 p-4 hover:bg-gray-50 transition-colors text-left"
								>
									<img
										src={card.designUrl}
										alt={card.title || t('engagement.cardAlt')}
										className="w-16 h-12 object-cover rounded-lg border border-gray-200"
									/>
									<div className="flex-1 min-w-0">
										<p className="text-sm font-medium text-gray-900 truncate">{card.title || t('engagement.untitled')}</p>
										<p className="text-xs text-gray-500">
											{card.status} {card.createdAt ? `- ${new Date(card.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` : ''}
										</p>
									</div>
									<svg
										className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${expandedCard === card._id ? 'rotate-180' : ''}`}
										fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"
									>
										<path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
									</svg>
								</button>

								{expandedCard === card._id && (
									<div className="border-t border-gray-100 p-4 bg-gray-50">
										{cardEngagement[card._id]?.loading ? (
											<div className="flex justify-center py-6">
												<img src="https://s3.us-east-2.amazonaws.com/cardclan.io-assets/loader-cardclan-nm.svg" width="40" alt={t('engagement.loading')} />
											</div>
										) : (
											<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
												{/* Analytics */}
												<div className="bg-white rounded-lg p-4 border border-gray-200">
													<h4 className="text-sm font-medium text-gray-700 mb-3">{t('engagement.analytics')}</h4>
													<div className="space-y-2">
														<div className="flex justify-between items-center">
															<span className="text-sm text-gray-500">{t('engagement.statEmailsOpened')}</span>
															<span className="text-sm font-semibold text-gray-900">{cardEngagement[card._id]?.emailOpens || 0}</span>
														</div>
														<div className="flex justify-between items-center">
															<span className="text-sm text-gray-500">{t('engagement.statCardsOpened')}</span>
															<span className="text-sm font-semibold text-gray-900">{cardEngagement[card._id]?.cardOpens || 0}</span>
														</div>
													</div>
												</div>

												{/* Reactions */}
												<div className="bg-white rounded-lg p-4 border border-gray-200">
													<h4 className="text-sm font-medium text-gray-700 mb-3">{t('engagement.reactions')}</h4>
													{cardEngagement[card._id]?.reactions && Object.keys(cardEngagement[card._id].reactions).length > 0 ? (
														<div className="flex flex-wrap gap-2">
															{Object.entries(cardEngagement[card._id].reactions).map(([emoji, count]: [string, any]) => (
																count > 0 && (
																	<span key={emoji} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-gray-200 bg-gray-50 text-sm">
																		<span className="text-base leading-none">{emoji}</span>
																		<span className="text-xs font-semibold text-indigo-600">{count}</span>
																	</span>
																)
															))}
														</div>
													) : (
														<p className="text-sm text-gray-400">{t('engagement.noReactions')}</p>
													)}
												</div>

												{/* Comments */}
												<div className="bg-white rounded-lg p-4 border border-gray-200">
													<h4 className="text-sm font-medium text-gray-700 mb-3">
														{t('engagement.comments', { count: cardEngagement[card._id]?.comments?.length || 0 })}
													</h4>
													{cardEngagement[card._id]?.comments?.length > 0 ? (
														<div className="space-y-2 max-h-40 overflow-y-auto">
															{cardEngagement[card._id].comments.map((comment: any) => (
																<div key={comment._id} className="text-sm">
																	<span className="font-medium text-gray-700">
																		{comment.isAnonymous ? t('engagement.anonymous') : comment.authorName || comment.authorEmail}
																	</span>
																	<p className="text-gray-500 text-xs mt-0.5">{comment.commentText || comment.text}</p>
																</div>
															))}
														</div>
													) : (
														<p className="text-sm text-gray-400">{t('engagement.noComments')}</p>
													)}
												</div>
											</div>
										)}
									</div>
								)}
							</div>
						))}
					</div>
				)}
			</div>
		</div>
	);
};

export default EngagementPage;
